import React from "react";
import { Line } from "react-chartjs-2";
import type { TooltipItem } from "chart.js";
import { tightAxis } from "../../utils/timeseriesChart";
import { ENGAGEMENTS_COLOR, VIEWS_COLOR, formatCompact, lineDataset } from "./chartTheme";
import { ChartArea } from "./TimeseriesChartFrame";

/*
 * The phone chart's plot, shared by the Monthly and Weekly views so they look the same: views on the
 * left axis, engagements on the right, both from 0 with five even levels that share gridlines
 * (tightAxis), scaled to the days on show. Tapping shows the day's values in the tooltip. A `null`
 * value is the blank slot after today in a period that isn't over yet; it draws nothing.
 */

const TICK_FONT = { family: "Poppins", size: 10 };

interface DailyLineChartProps {
	/** One x label per day ("" for the blank slot after today). */
	labels: string[];
	/** The tooltip title per day ("Mon 14 Sept"). */
	titles: string[];
	views: Array<number | null>;
	engagements: Array<number | null>;
	/** How many x labels may show; the rest are skipped evenly. */
	maxTicks: number;
}

const observed = (values: Array<number | null>) => values.filter((value): value is number => value !== null);

export const DailyLineChart: React.FC<DailyLineChartProps> = ({ labels, titles, views, engagements, maxTicks }) => {
	const axis = (values: Array<number | null>, color: string, position: "left" | "right") => {
		const { max, stepSize } = tightAxis(observed(values));
		return {
			type: "linear" as const,
			position,
			min: 0,
			max,
			ticks: {
				stepSize,
				color,
				font: TICK_FONT,
				callback: (value: string | number) => formatCompact(Number(value)),
			},
		};
	};
	// No dots until a day is touched.
	const points = { pointRadius: 0, pointHoverRadius: 4 };

	return (
		<ChartArea>
			<Line
				data={{
					labels,
					datasets: [
						{ ...lineDataset("Views", views, VIEWS_COLOR, "y"), ...points },
						{ ...lineDataset("Engagements", engagements, ENGAGEMENTS_COLOR, "y1"), ...points },
					],
				}}
				options={{
					responsive: true,
					maintainAspectRatio: false,
					interaction: { mode: "index", intersect: false },
					plugins: {
						legend: {
							position: "top",
							labels: {
								usePointStyle: true,
								pointStyle: "circle",
								font: { family: "Poppins", size: 12 },
								padding: 16,
								boxWidth: 8,
								boxHeight: 8,
							},
						},
						tooltip: {
							// The blank slot after today has no values, so it shows no tooltip.
							filter: (item: TooltipItem<"line">) => item.raw !== null,
							callbacks: {
								title: (items: TooltipItem<"line">[]) => (items[0] ? titles[items[0].dataIndex] : ""),
							},
						},
					},
					scales: {
						y: { ...axis(views, VIEWS_COLOR, "left"), grid: { color: "#f0f0f0" } },
						y1: { ...axis(engagements, ENGAGEMENTS_COLOR, "right"), grid: { drawOnChartArea: false } },
						// Kept level so the labels never collide.
						x: {
							ticks: {
								font: TICK_FONT,
								color: "#969696",
								maxTicksLimit: maxTicks,
								maxRotation: 0,
								autoSkipPadding: 12,
							},
							grid: { display: false },
						},
					},
				}}
			/>
		</ChartArea>
	);
};
